import {message} from 'antd';
import {Service} from 'typedi';
import {jwtDecode} from 'jwt-decode';
import axios, {AxiosResponse} from 'axios';
import i18n from '@/locales/i18n';
import {DTO, ResponseDTO} from '@/shared/base/dto';
import {
  ENDPOINT,
  HTTP_CODE,
  METHOD,
  STATUS_CODE,
  STORAGE_KEYS,
} from '@/constants';
import RSAUtils from '@/utils/rsa.util';

@Service()
export class HttpService {
  public access_token: string | undefined;

  constructor() {
    this.access_token =
      localStorage.getItem(STORAGE_KEYS.USER_ACCESS_TOKEN) || undefined;
    axios.interceptors.response.use(
      (response: AxiosResponse<ResponseDTO>) => {
        return response;
      },
      (error) => {
        if (error && error.response && error.response.data) {
          if (error?.response?.data?.statusCode === HTTP_CODE.UNAUTHORIZED) {
            localStorage.clear();
            this.access_token = undefined;
          }

          return error.response;
        }
        return Promise.reject(error.response);
      },
    );
  }

  public getTokenInfo(): {exp: number} | undefined {
    return this.access_token ? jwtDecode(this.access_token) : undefined;
  }

  public async request<T extends DTO>(dto: T): Promise<ResponseDTO> {
    try {
      if (dto.method === METHOD.POST) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if (
          (dto.body as any)?.password ||
          (dto.body as any)?.oldPassword ||
          (dto.body as any)?.newPassword
        ) {
          const publicKey = localStorage.getItem(STORAGE_KEYS.RSA_PUBLIC_KEY);
          if (publicKey) {
            dto.body = RSAUtils.encryptRequestBody2Payloads(
              dto.body,
              publicKey,
            );
          }
        }
      }
      const timezoneOffset = new Date().getTimezoneOffset();
      const response = await axios({
        method: dto.method,
        headers: {
          Authorization: this.access_token ? 'Bearer ' + this.access_token : '',
          'X-Frame-Options': 'DENY',
          'Content-Security-Policy': "frame-ancestors 'self';",
          lang: `${i18n.resolvedLanguage}`,
          'X-Timezone': timezoneOffset.toString(),
          ...dto.headers,
          'ngrok-skip-browser-warning': '69420',
        },
        baseURL: process.env.REACT_APP_BACKEND + ENDPOINT.BASE_URL,
        url: this.replaceURLToParamURL(dto.url, dto.param || {}),
        data: dto.body,
        params: dto.query,
        responseType: dto.responseType,
      });
      if (response.status === HTTP_CODE.INTERNAL_SERVER_ERROR) {
        message.error(response.data?.message);
      }
      if (
        response.status !== HTTP_CODE.OK &&
        response.status !== HTTP_CODE.CREATED
      ) {
        return {
          data: response?.data,
          msgSts: response?.data?.msgSts || {
            status: response.status,
            code: STATUS_CODE.FAILED,
            statusCode: response.data?.statusCode || 0,
            message: response.data?.message,
          },
        };
      } else {
        return {
          data: response.data || response.data,
          msgSts: response.data.msgSts || {
            code: STATUS_CODE.SUCCESS,
            status: response.status,
            statusCode: response.data?.statusCode || 0,
            message: 'Get data success',
            note: response.headers?.note,
          },
        };
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (errorResponse: any) {
      return {
        data: errorResponse?.data?.data,
        msgSts: errorResponse?.data?.msgSts || {
          status: errorResponse.status,
          code: STATUS_CODE.FAILED,
          statusCode: -1,
          message: 'Get data fail',
        },
      };
    }
  }

  private replaceURLToParamURL = (url: string, param: object): string => {
    let newUrl = url;
    Object.entries(param).forEach(([key, value]) => {
      newUrl = newUrl.replace(':' + key.toString(), value.toString());
    });
    return newUrl;
  };
}
